import React from 'react';
import { Controller, useForm, useFormContext } from 'react-hook-form';
import { ModalHeader } from '../../../../../../CommonComponents/OPL/Modal/ModalHeader';
import { ModalCloseButton } from '../../../../../../CommonComponents/OPL/Modal/ModalCloseButton';
import { Modal } from '../../../../../../CommonComponents/OPL/Modal/Modal';
import { ModalFooter } from '../../../../../../CommonComponents/OPL/Modal/ModalFooter';
import { Select } from '../../../../../../CommonComponents/OPL/Select/Select/Select';
import { Text } from '../../../../../../CommonComponents/OPL/Text/Text';
import { HtmlTable } from '../../../../../../CommonComponents/OPL/HtmlTable/HtmlTable';
import { Checkbox } from '../../../../../../CommonComponents/OPL/Checkbox/Checkbox';
import { formatCurrency } from '../../../../../../CommonUtils/formatNumber';
import { Box } from '../../../../../../CommonComponents/OPL/Box/Box';
import { Button } from '../../../../../../CommonComponents/OPL/Button/Button';
import { GliaGlobalStyle, TableHeader, Wrapper } from './MoveHoldingsDrawer.styles';
import { HoldingDTO } from '../../../AssetBucketingResults/api/useAssetBucketingResultsQuery';
import { moveHoldings } from '../../utils/moveHoldings';
import { FormValues as AssetBucketingEditFormValues } from '../../AssetBucketingEdit.meta';

export interface AssetToMoveHoldings {
  id: string;
  name: string;
  holdings: HoldingDTO[];
  bucketIndex: number;
}

interface FormValues {
  destinationBucketIndex: number;
  holdingIds: string[];
}

interface Props {
  asset: AssetToMoveHoldings;
  onClose(): void;
}

export const MoveHoldingsDrawer = ({ asset, onClose }: Props) => {
  const assetBucketingEditFormBag = useFormContext<AssetBucketingEditFormValues>();

  const buckets = assetBucketingEditFormBag.watch('buckets');

  const bucketOptions = buckets.reduce((acc, { title }, index) => {
    if (index !== asset.bucketIndex) {
      acc.push({
        label: title,
        value: index,
      });
    }

    return acc;
  }, [] as { label: string; value: number }[]);

  const { control, register, handleSubmit, setValue, watch } = useForm<FormValues>({
    defaultValues: {
      destinationBucketIndex: bucketOptions[0].value,
      holdingIds: [],
    },
  });

  const holdingIds = watch('holdingIds');

  const onSubmit = (values: FormValues) => {
    const newBuckets = moveHoldings({
      ...values,
      assetId: asset.id,
      sourceBucketIndex: asset.bucketIndex,
      buckets,
    });

    assetBucketingEditFormBag.setValue('buckets', newBuckets);

    onClose();
  };

  return (
    <>
      <GliaGlobalStyle />

      <Modal
        isOpen
        variant="drawer"
        drawerPlacement="right"
        onRequestClose={onClose}
        contentLabel="Move holdings"
      >
        <ModalHeader css="padding-left: 16px">{asset.name}</ModalHeader>
        <ModalCloseButton onClick={onClose} />

        <Wrapper
          as="form"
          onSubmit={evt => {
            evt.preventDefault();
            evt.stopPropagation();
            handleSubmit(onSubmit)(evt);
          }}
          aria-label="Move holdings form"
        >
          <Box flex={1} overflowY="auto">
            <Box pt={2} px="16" pb={20}>
              <Controller
                control={control}
                name="destinationBucketIndex"
                render={({ field }) => (
                  <Select
                    label="Move holding(s) to"
                    value={bucketOptions.find(({ value }) => value === field.value)}
                    onChange={option => field.onChange(option?.value)}
                    options={bucketOptions}
                    placeholder="Select destination bucket..."
                    isDisabled={bucketOptions.length === 1}
                    isPortal
                  />
                )}
              />
            </Box>

            <HtmlTable variant="embedded" columnSpacing="small">
              <table>
                <colgroup>
                  <col />
                  <col width="100%" />
                  <col />
                </colgroup>

                <TableHeader>
                  <tr>
                    <th>
                      <Checkbox
                        checked={holdingIds.length === asset.holdings.length}
                        onChange={evt => {
                          setValue(
                            'holdingIds',
                            evt.target.checked ? asset.holdings.map(({ id }) => id) : [],
                          );
                        }}
                        aria-label="Select all holdings"
                      />
                    </th>
                    <th>
                      <Text variant="smallCaps" textAlign="left">
                        Security
                      </Text>
                    </th>
                    <th>
                      <Text variant="smallCaps" textAlign="right">
                        Market Value
                      </Text>
                    </th>
                  </tr>
                </TableHeader>

                <tbody>
                  {asset.holdings.map(holding => (
                    <tr key={holding.id}>
                      <td>
                        <Checkbox
                          {...register('holdingIds')}
                          value={holding.id}
                          aria-label={`Select holding ${holding.name}`}
                        />
                      </td>

                      <td>
                        <Box display="flex" flexDirection="column" css="gap: 2px">
                          <Text textAlign="left" fontWeight={500}>
                            {holding.ticker}
                          </Text>
                          <Text textAlign="left" fontSize="small" color="neutral.600">
                            {holding.name}
                          </Text>
                        </Box>
                      </td>

                      <td>
                        <Text variant="tabular" textAlign="right" fontWeight={500}>
                          {formatCurrency(holding.value, { mantissa: 2 })}
                        </Text>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </HtmlTable>
          </Box>

          <ModalFooter hasBorder>
            {holdingIds.length > 0 && (
              <Text fontWeight={500} color="neutral.600" mr="auto">
                {holdingIds.length} Item(s) Selected
              </Text>
            )}

            <Button variant="subtleInactive" onClick={onClose}>
              Cancel
            </Button>

            <Button ml="8" type="submit" disabled={holdingIds.length === 0}>
              Move Holding(s)
            </Button>
          </ModalFooter>
        </Wrapper>
      </Modal>
    </>
  );
};
